// Browser entry for the server build: talks to the API over HTTP.
import { boot, type Transport } from './app';

const OFFLINE = 'You are offline. Your lessons will load when you are back online.';

const http: Transport = {
  label: 'http',
  async call(method, path, body) {
    let res: Response;
    try {
      res = await fetch(path, {
        method,
        credentials: 'same-origin',
        headers: { 'x-whh': '1', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      // The network is unreachable (the cached shell opened offline): show the app's error state, not a blank page.
      throw new Error(OFFLINE);
    }
    let data: any = null;
    try { data = await res.json(); } catch { data = { error: 'Unexpected response' }; }
    return { status: res.status, body: data };
  },
};

// ---------- install prompt (Chrome, Edge, Android): kept for the Get the app screen ----------
declare global { interface Window { __whhInstall?: any; __whhInstalled?: boolean } }
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.__whhInstall = e;
  window.dispatchEvent(new Event('whh-install-ready'));
});
window.addEventListener('appinstalled', () => {
  window.__whhInstalled = true;
  window.__whhInstall = undefined;
  window.dispatchEvent(new Event('whh-install-ready'));
});

// ---------- service worker: every visitor, so the shell is cached and updates roll out ----------
function showUpdateToast(worker: ServiceWorker) {
  if (document.querySelector('.update-toast')) return;
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'toast update-toast';
  el.setAttribute('role', 'status');
  el.innerHTML = '<div class="orb" style="width:22px;height:22px;flex-shrink:0"></div><span>A new version is ready. Tap to refresh.</span>';
  el.addEventListener('click', () => { el.disabled = true; worker.postMessage({ type: 'SKIP_WAITING' }); });
  document.body.appendChild(el);
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || location.protocol === 'about:') return;
  let reloading = false;
  // The new worker took over after the member tapped the toast: load the new shell once (the flag stops a reload loop).
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshRequested() || reloading) return;
    reloading = true;
    location.reload();
  });
  navigator.serviceWorker.register('/sw.js').then((reg) => {
    const offer = (w: ServiceWorker | null) => { if (w && navigator.serviceWorker.controller) showUpdateToast(w); };
    offer(reg.waiting);
    reg.addEventListener('updatefound', () => {
      const w = reg.installing;
      w?.addEventListener('statechange', () => { if (w.state === 'installed') offer(w); });
    });
    // An app that stays open on the Home Screen checks for a new version whenever it comes back to the front.
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') reg.update().catch(() => {}); });
  }).catch(() => { /* no service worker: the portal still works online */ });
}
// The reload is only for a tap on the update toast (the tap disables the toast).
const refreshRequested = () => !!(document.querySelector('.update-toast') as HTMLButtonElement | null)?.disabled;

if (document.readyState === 'complete') registerServiceWorker();
else window.addEventListener('load', registerServiceWorker);

boot(document.getElementById('app')!, http);
